import React from "react";
import "./ClubPage.css";
import { useTranslation } from "react-i18next";
import {
  FiTrendingUp,
  FiShield,
  FiFilter,
  FiUsers,
  FiStar,
  FiZap,
  FiCheck,
  FiFileText,
} from "react-icons/fi";
import { AMBASSADORS, DEFAULT_LINKS } from "../../config/ambassadors";

const ClubPage = ({ setActiveTab }) => {
  const { t, i18n } = useTranslation();

  const benefits = [
    { icon: <FiTrendingUp />, key: "1" },
    { icon: <FiShield />, key: "2" },
    { icon: <FiFilter />, key: "3" },
    { icon: <FiUsers />, key: "4" },
    { icon: <FiStar />, key: "5" },
    { icon: <FiZap />, key: "6" },
  ];

  const getPaymentLink = (planType) => {
    const currentLang = i18n.language?.startsWith("en") ? "en" : "fr";
    const ref = localStorage.getItem("ambassador_ref");

    if (ref && AMBASSADORS[ref] && AMBASSADORS[ref].links[planType]) {
      const ambLink = AMBASSADORS[ref].links[planType];
      return typeof ambLink === "object" ? ambLink[currentLang] : ambLink;
    }

    const defaultLink = DEFAULT_LINKS[planType];
    return typeof defaultLink === "object"
      ? defaultLink[currentLang]
      : defaultLink;
  };

  return (
    <div className="club-page-container fade-in">
      {/* 1. HERO */}
      <section className="club-hero">
        <h1>{t("club_page.hero.title")}</h1>
        <p>{t("club_page.hero.subtitle")}</p>
      </section>

      {/* 2. AVANTAGES */}
      <section className="club-benefits">
        <h2 className="section-title text-center">
          {t("club_page.benefits.title")}
        </h2>
        <div className="benefits-grid">
          {benefits.map((b, index) => (
            <div className="benefit-card" key={index}>
              <div className="benefit-icon">{b.icon}</div>
              <h3>{t(`club_page.benefits.${b.key}.title`)}</h3>
              <p>{t(`club_page.benefits.${b.key}.desc`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 3. FOUNDER */}
      <section className="founder-section">
        <img
          src="https://d1yei2z3i6k35z.cloudfront.net/8795695/66ab953fa25a5_Designsanstitre1.jpg"
          alt="Founder"
          className="founder-img"
        />
        <div className="founder-content">
          <h4>{t("club_page.founder.role")}</h4>
          <h2>{t("club_page.founder.name")}</h2>
          <p className="founder-quote">"{t("club_page.founder.quote")}"</p>
        </div>
      </section>

      {/* 4. PRICING */}
      <section className="pricing-section">
        <h2 className="section-title">{t("club_page.pricing.title")}</h2>
        <div className="pricing-grid">
          {/* INVESTOR */}
          <div className="pricing-card">
            <span className="badge-target">
              {t("club_page.pricing.investor.target")}
            </span>

            <div className="pricing-header">
              <h3>{t("club_page.pricing.investor.name")}</h3>
              <div className="price">
                {t("club_page.pricing.investor.price")}
                <span>{t("club_page.pricing.per_year")}</span>
              </div>
            </div>

            <p className="pricing-quote">
              "{t("club_page.pricing.investor.quote")}"
            </p>

            <ul className="features-list">
              {(
                t("club_page.pricing.investor.features", {
                  returnObjects: true,
                }) || []
              ).map((feat, i) => (
                <li key={i}>
                  <FiCheck className="check-icon" /> {feat}
                </li>
              ))}
              <li className="feature-highlight">
                <FiCheck className="check-icon" />{" "}
                {t("club_page.pricing.investor.highlight")}
              </li>
            </ul>

            <p className="pricing-note">
              {t("club_page.pricing.investor.note")}
            </p>

            <a
              href={getPaymentLink("investor")}
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-btn"
            >
              {t("club_page.pricing.subscribe")}
            </a>
          </div>

          {/* INDUSTRY */}
          <div className="pricing-card industry-card">
            <span className="badge-target">
              {t("club_page.pricing.industry.target")}
            </span>

            <div className="pricing-header">
              <h3>{t("club_page.pricing.industry.name")}</h3>
              <div className="price">
                {t("club_page.pricing.industry.price")}
                <span>{t("club_page.pricing.per_year")}</span>
              </div>
            </div>

            <div className="badge-limit-wrapper">
              <span className="badge-limit">
                {t("club_page.pricing.industry.limit")}
              </span>
            </div>

            <ul className="features-list">
              {(
                t("club_page.pricing.industry.features", {
                  returnObjects: true,
                }) || []
              ).map((feat, i) => (
                <li key={i}>
                  <FiCheck className="check-icon" /> {feat}
                </li>
              ))}
            </ul>
            <a
              href={getPaymentLink("industry")}
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-btn"
            >
              {t("club_page.pricing.subscribe")}
            </a>
          </div>

          {/* BUSINESS CIRCLE */}
          <div className="pricing-card featured-card">
            <span className="badge-target">
              {t("club_page.pricing.circle.target")}
            </span>

            <div className="pricing-header">
              <h3>{t("club_page.pricing.circle.name")}</h3>
              <div className="price">
                {t("club_page.pricing.circle.price")}
                <span>{t("club_page.pricing.per_year")}</span>
              </div>
            </div>

            <p className="pricing-quote">
              "{t("club_page.pricing.circle.quote")}"
            </p>

            <ul className="features-list">
              {(
                t("club_page.pricing.circle.features", {
                  returnObjects: true,
                }) || []
              ).map((feat, i) => (
                <li key={i}>
                  <FiCheck className="check-icon" /> {feat}
                </li>
              ))}
              <li className="feature-highlight">
                <FiCheck className="check-icon" />{" "}
                {t("club_page.pricing.circle.highlight")}
              </li>
            </ul>

            <p className="pricing-note">
              {t("club_page.pricing.circle.note")}
            </p>

            <a
              href={getPaymentLink("circle")}
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-btn"
            >
              {t("club_page.pricing.subscribe")}
            </a>
          </div>

          {/* PRIVATE */}
          <div className="pricing-card">
            <span className="badge-target">
              {t("club_page.pricing.private.target")}
            </span>

            <div className="pricing-header">
              <h3>{t("club_page.pricing.private.name")}</h3>
              <div className="price">
                {t("club_page.pricing.private.price")}
                <span>{t("club_page.pricing.per_year")}</span>
              </div>
            </div>

            <p className="pricing-quote">
              "{t("club_page.pricing.private.quote")}"
            </p>

            <ul className="features-list">
              {(
                t("club_page.pricing.private.features", {
                  returnObjects: true,
                }) || []
              ).map((feat, i) => (
                <li key={i}>
                  <FiCheck className="check-icon" /> {feat}
                </li>
              ))}
              <li className="feature-highlight">
                <FiCheck className="check-icon" />{" "}
                {t("club_page.pricing.private.highlight")}
              </li>
            </ul>

            <p className="pricing-note">
              {t("club_page.pricing.private.note")}
            </p>

            <a
              href={getPaymentLink("private")}
              target="_blank"
              rel="noopener noreferrer"
              className="pricing-btn"
            >
              {t("club_page.pricing.apply")}
            </a>
          </div>
        </div>

        <button
          type="button"
          className="cgv-note-banner"
          onClick={() => setActiveTab && setActiveTab("cgv")}
        >
          <FiFileText />
          {i18n.language?.startsWith("en")
            ? "Read the Terms & Conditions (CGV)"
            : "Consulter les Conditions générales d'adhésion (CGV)"}
        </button>
      </section>
    </div>
  );
};

export default ClubPage;
